import { CSSResultGroup, html, LitElement, nothing, TemplateResult } from 'lit'
import { customElement, property, state } from 'lit/decorators.js'
import { style } from './column-menu.style.js'
import { hostStyle } from '@cloudsuite/mosaic-shared/styles/host.style.js'
import { Navigation, NavigationItem } from 'components/discovery/navigation-menu/typing/navigation-types.js'
import { classMap } from 'lit/directives/class-map.js'
import 'components/discovery/navigation-menu/components/navigation-menu-column/navigation-menu-column.js'

export type AllProductsLinkPositionSubLevel = 'top' | 'bottom'

const getMenuImage = (item: NavigationItem | null | undefined): string | null => {
  return item?.menu_picture || item?.picture || null
}

const resolveFirstColumn = (navItems: Navigation | NavigationItem): { items: Navigation; parent: NavigationItem | null } => {
  if (Array.isArray(navItems)) {
    return { items: navItems, parent: null }
  }
  return { items: navItems.childs ?? [], parent: navItems }
}

@customElement('csc-column-menu')
export class CSCColumnMenu extends LitElement {
  static override styles: CSSResultGroup = [hostStyle, style]

  @property({ type: Array, attribute: 'nav-items' })
  navItems: Navigation | NavigationItem = []

  @property({ type: Number, attribute: 'max-depth' })
  maxDepth: number = 5

  @property({ type: Boolean, attribute: 'show-asset-main-level' })
  showAssetMainLevel: boolean = false

  @property({ type: Boolean, attribute: 'show-asset-sub-level' })
  showAssetSubLevel: boolean = false

  @property({ type: Boolean, attribute: 'show-main-title' })
  showMainTitle: boolean = false

  @property({ type: Boolean, attribute: 'show-title-sub-level' })
  showTitleSubLevel: boolean = false

  @property({ type: String, attribute: 'all-products-link-position-sub-level' })
  allProductsLinkPositionSubLevel: AllProductsLinkPositionSubLevel = 'top'

  @property({ type: Boolean, attribute: 'include-cateogory-name-sub-level' })
  includeCategoryNameSubLevel: boolean = false

  @property({ type: String, attribute: 'asset-width' })
  assetWidth: string = '24'

  @property({ type: String, attribute: 'asset-height' })
  assetHeight: string = '24'

  @property({ type: Boolean })
  dividers: boolean = false

  @property({ type: String, attribute: 'default-image' })
  defaultImage: string = ''

  @property({ type: Boolean, attribute: 'show-image-main-level' })
  showImageMainLevel: boolean = false

  @property({ type: String, attribute: 'image-main-level-width' })
  imageMainLevelWidth: string = '150'

  @property({ type: String, attribute: 'image-main-level-height' })
  imageMainLevelHeight: string = '150'

  @state()
  private activeItems: (NavigationItem | null)[] = []

  private get currentImage(): string | null {
    for (let i = this.activeItems.length - 1; i >= 0; i--) {
      const image = getMenuImage(this.activeItems[i])
      if (image) {
        return image
      }
    }
    return null
  }

  override connectedCallback(): void {
    super.connectedCallback()
    this.activeItems = new Array(this.maxDepth).fill(null)
  }

  private handleItemSelect(depth: number, event: CustomEvent<NavigationItem>): void {
    const selectedItem = event.detail

    const newActiveItems = [...this.activeItems]
    newActiveItems[depth] = selectedItem

    for (let i = depth + 1; i < newActiveItems.length; i++) {
      newActiveItems[i] = null
    }
    this.activeItems = newActiveItems
  }

  private handleColumnMouseLeave(depth: number): void {
    const hasDeeperActiveItems = this.activeItems.slice(depth + 1).some(item => item !== null)
    if (depth < this.activeItems.length - 1 && hasDeeperActiveItems) {
      const newActiveItems = [...this.activeItems]
      for (let i = depth + 1; i < newActiveItems.length; i++) {
        newActiveItems[i] = null
      }
      this.activeItems = newActiveItems
    }
  }

  private renderImage(): TemplateResult | typeof nothing {
    if (!this.showImageMainLevel || !this.currentImage) {
      return nothing
    }

    return html`
      <div class="image-wrapper">
        <cse-image
          source="${this.currentImage}"
          fallback-source="${this.defaultImage}"
          loading="lazy"
          width="${this.imageMainLevelWidth}"
          height="${this.imageMainLevelHeight}"
        ></cse-image>
      </div>
    `
  }

  override render(): TemplateResult {
    const columnsToRender: { items: Navigation; parent?: NavigationItem | null; depth: number }[] = []

    // First column (depth 0)
    const firstColumn = resolveFirstColumn(this.navItems ?? [])
    if (firstColumn.items.length > 0) {
      columnsToRender.push({ ...firstColumn, depth: 0 })
    }

    // Subsequent columns
    for (let depth = 0; depth < this.maxDepth - 1; depth++) {
      const activeItem = this.activeItems[depth]
      if (activeItem && activeItem.childs && activeItem.childs.length > 0) {
        columnsToRender.push({ items: activeItem.childs, parent: activeItem, depth: depth + 1 })
      } else {
        break
      }
    }

    const containerClasses = {
      'container': true,
      'with-image': this.showImageMainLevel
    }

    return html`
      <div class="${classMap(containerClasses)}" style="--csc-column-menu-max-depth: ${this.maxDepth};">
        ${columnsToRender.map(({ items, parent, depth }) => {
          const showAssetForColumn = depth === 0 ? this.showAssetMainLevel : this.showAssetSubLevel
          return html`
            <div
              class="column-wrapper ${depth === 0 ? 'first-column' : ''}"
              @mouseleave="${() => this.handleColumnMouseLeave(depth)}"
            >
              <csc-navigation-menu-column
                navigation-items="${JSON.stringify(items)}"
                active-item-id="${this.activeItems[depth]?.id || null}"
                ?show-title="${this.showMainTitle}"
                ?dividers="${this.dividers}"
                .isSubLevel="${depth > 0}"
                .parentItem="${parent ?? null}"
                ?show-asset="${showAssetForColumn}"
                asset-width="${this.assetWidth}"
                asset-height="${this.assetHeight}"
                ?include-cateogory-name="${this.includeCategoryNameSubLevel}"
                ?show-title-sub-level="${this.showTitleSubLevel}"
                default-image="${this.defaultImage}"
                all-products-link-position="${this.allProductsLinkPositionSubLevel}"
                @item-select="${(e: CustomEvent<NavigationItem>) => this.handleItemSelect(depth, e)}"
              ></csc-navigation-menu-column>
            </div>
          `
        })}
        ${this.renderImage()}
      </div>
    `
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'csc-column-menu': CSCColumnMenu
  }
}
